/**
 * Runtime config: uses window.APP_CONFIG (from Config.js at deploy time) when available,
 * otherwise falls back to static config.json.
 *
 * Phone / LAN browser (e.g. http://192.168.1.74:3000):
 *   If SERVER_URL is 127.0.0.1, rewrite API host to the page host.
 *
 * Capacitor native shell (WebView origin is https://localhost):
 *   127.0.0.1 inside the device/emulator is NOT your PC.
 *   Android emulator → 10.0.2.2 (special alias to the host machine).
 *   Physical phone → set APP_CONFIG.NATIVE_API_HOST or localStorage.nativeApiHost
 *   to your PC LAN IP (e.g. "192.168.1.74"). API must listen on 0.0.0.0:5055.
 */
import { Capacitor } from '@capacitor/core';
import staticConfig from './config.json';

const runtime =
    typeof window !== 'undefined' && window.APP_CONFIG ? window.APP_CONFIG : {};
const merged = { ...staticConfig, ...runtime };

function isLoopbackHost(hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1';
}

function isAndroidEmulator() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /sdk_gphone|Emulator|Android SDK built for|goldfish|ranchu/i.test(ua);
}

/** Explicit override for physical devices (and optional emulator). */
function getNativeApiHostOverride() {
    if (typeof window === 'undefined') return null;

    const fromConfig =
        (window.APP_CONFIG && window.APP_CONFIG.NATIVE_API_HOST) ||
        window.NATIVE_API_HOST;
    if (fromConfig && String(fromConfig).trim()) {
        return String(fromConfig).trim();
    }

    try {
        const stored = window.localStorage.getItem('nativeApiHost');
        if (stored && stored.trim()) return stored.trim();
    } catch (_) {
        /* ignore */
    }

    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
        return null;
    }

    // Emulator → host PC alias
    if (isAndroidEmulator()) {
        return '10.0.2.2';
    }

    // Physical phone over USB: use with `adb reverse tcp:5055 tcp:5055`
    // (maps phone localhost → PC API). For Wi‑Fi-only, set NATIVE_API_HOST to LAN IP.
    return '127.0.0.1';
}

function rewriteLocalApiHost(url) {
    if (typeof window === 'undefined' || !url) return url;
    try {
        const parsed = new URL(url, window.location.origin);
        if (!isLoopbackHost(parsed.hostname)) return url;

        const pageHost = window.location.hostname;

        // Browser on phone/LAN: page is already the PC IP
        if (!isLoopbackHost(pageHost)) {
            parsed.hostname = pageHost;
            return parsed.href;
        }

        // Capacitor WebView (localhost): map loopback API to a host-reachable address
        if (Capacitor.isNativePlatform()) {
            const nativeHost = getNativeApiHostOverride();
            if (nativeHost) {
                parsed.hostname = nativeHost;
                return parsed.href;
            }
        }
    } catch (_) {
        /* keep original */
    }
    return url;
}

merged.SERVER_URL = rewriteLocalApiHost(merged.SERVER_URL);
merged.SERVER_URL_IMGUpload = rewriteLocalApiHost(merged.SERVER_URL_IMGUpload);

export default merged;
